import classNames from 'classnames'
import { WarningHexagon } from 'iconoir-react'
import { LazyLocationCell } from 'app/components/LazyLocationCell'
import NameIcon from 'app/components/shared/NameIcon'
import { Button } from 'app/components/ui/button'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { DuplicateMatch } from 'app/lib/contactDuplicates'
import { initials } from 'app/lib/contactQuickEntry'

/**
 * Two tiers. `blocking` means the name matches as well as the contact method, so
 * it is the same person and Save is disabled: the way forward is the record that
 * already exists. Everything else is advisory and dismissible, because a shared
 * cell phone with a different name is usually a family on one mobile.
 */
export function DuplicateBanner({
  match: { contact, blocking },
  onOpen,
  onDismiss,
}: {
  match: DuplicateMatch
  onOpen: () => void
  onDismiss: () => void
}) {
  return (
    <div
      className={classNames(
        `overflow-hidden rounded-lg border`,
        blocking ? `border-red-300 bg-red-50` : `border-amber-300 bg-amber-50`,
      )}
    >
      <div className="flex items-start gap-2 px-3 pb-2 pt-3">
        <WarningHexagon
          className={classNames(`mt-0.5 h-4 w-4 shrink-0`, blocking ? `text-red-600` : `text-amber-600`)}
        />
        <span className={classNames(`text-ui-sm`, blocking ? `text-red-900` : `text-amber-900`)}>
          {blocking
            ? `This contact already exists at this location. Open it instead of creating a second one.`
            : `A contact at this location already has this cell phone or email.`}
        </span>
      </div>
      <div className="mx-3 flex items-center gap-2 rounded-lg border border-default bg-surface p-3">
        <NameIcon fullName={initials(contact.name)} heightClassName="h-8" widthClassName="w-8" isTiny />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-ui-md">{contact.name}</span>
          <span className="truncate text-body-xs text-subtle">
            {[contact.leadStatus, contact.cellPhone && formatPhoneNumber(contact.cellPhone), contact.email]
              .filter(Boolean)
              .join(` · `)}
          </span>
        </div>
        {contact.locationId && (
          <LazyLocationCell locationId={contact.locationId} className="ml-auto text-body-xs text-subtle" />
        )}
      </div>
      <div className="flex gap-2 px-3 pb-3 pt-2">
        <Button type="button" variant="white" size="xs" onClick={onOpen}>
          Open this contact
        </Button>
        {!blocking && (
          <Button type="button" variant="subtle" size="xs" onClick={onDismiss}>
            Not a match
          </Button>
        )}
      </div>
    </div>
  )
}
